import { useErrorStore } from '../zustand/errors.store';
import AxiosInstance from '../config/axios';
import { getRequestErrorLog } from '../helpers/internUtils';
import { ErrorLog } from '../types/errors.interface';
import { useApiStore } from '../zustand/api.store';

const { addError } = useErrorStore.getState();

const getCountryFromUrl = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  const country = urlParams.get('country');
  return country ? country.toLowerCase() : 'es';
};

// Gets user data from URL parameters if available, otherwise fetches from API
export const getUserData = async () => {
  const { markApiCallsAsCompleted } = useApiStore.getState();
  const { setUserEmail } = useErrorStore.getState();

  // Try to get email and language from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const email = urlParams.get('email');
  const language = urlParams.get('language');

  // If  email is present in URL, return it
  if (email) {
    markApiCallsAsCompleted(1);
    setUserEmail(email);
    return { email, language: language || 'en' };
  }

  // Otherwise, fetch from API
  try {
    const response = await AxiosInstance.get('/users/me/');
    if (response && response.status === 200) {
      markApiCallsAsCompleted(1);
      // console.log('getUserData ok - 1', response.data);
      setUserEmail(response.data.email);
      return response.data;
    }
  } catch (error: any) {
    const errorLog = getRequestErrorLog(error, '/users/me/', null);
    addError(errorLog);
    return null;
  }
};

// Gets selected style ID from API, or returns null if editable=True in URL
export const getSelectedStyle = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const editable = urlParams.get('editable');
  const { markApiCallsAsCompleted } = useApiStore.getState();

  // If editable=True in URL, skip API call and return null
  if (editable && editable.toLowerCase() === 'true') {
    markApiCallsAsCompleted(2);
    return [
      {
        id: 1,
        name: 'Estilo por defecto uDA',
        is_selected: true,
      },
    ];
  }

  try {
    const response = await AxiosInstance.get(
      '/b4p/smart-valuation/report-style/'
    );
    if (response && response.status === 200) {
      markApiCallsAsCompleted(2);
      // console.log('getSelectedStyle ok - 2', response.data);
      const styles = response.data;
      const selectedStyle = styles.filter(
        (style: any) => style.is_selected === true
      );

      return selectedStyle[0].id;
    }
  } catch (error: any) {
    const errorLog = getRequestErrorLog(
      error,
      '/b4p/smart-valuation/report-style/',
      null
    );
    addError(errorLog);
    return null;
  }
};

// Gets user report data from API, or returns default data if editable=True in URL
export const getUserReportData = async (reportId: string) => {
  const urlParams = new URLSearchParams(window.location.search);
  const editable = urlParams.get('editable');

  const { markApiCallsAsCompleted } = useApiStore.getState();

  // If editable=True in URL, skip API call and return default data
  if (editable && editable.toLowerCase() === 'true') {
    markApiCallsAsCompleted(3);
    return {
      name: 'Estilo por defecto uDA',
      logo: null,
      company: '',
      address: 'Calle Orense 6, planta 2',
      city: '28020 Madrid',
      fullname: null,
      phone: null,
      email: 'support.uda@accumin.com',
      description: null,
      image: null,
      exclude_rent: false,
      exclude_sale: false,
    };
  }

  try {
    const response = await AxiosInstance.get(
      `/b4p/smart-valuation/report-style/${reportId}/`
    );
    if (response && response.status === 200) {
      markApiCallsAsCompleted(3);
      // console.log('getUserReportData ok - 3', response.data);
      return response.data;
    }
  } catch (error) {
    const errorLog = getRequestErrorLog(
      error,
      '/b4p/smart-valuation/report-style/',
      reportId
    );
    addError(errorLog);
    return null;
  }
};

export const getInitialIndicators = async (params: any) => {
  const { markApiCallsAsCompleted } = useApiStore.getState();
  const country = getCountryFromUrl();
  const paramsWithCountry = { ...params, country };
  const parsedParams = new URLSearchParams(paramsWithCountry).toString();
  try {
    const response = await AxiosInstance.get(
      `/api/indicators/v2/boundary/?boundary_type=15&${parsedParams}`
    );
    if (response && response.status === 200) {
      markApiCallsAsCompleted(4);
      // console.log('getInitialIndicators ok - 4', response.data);
      return response.data;
    }
  } catch (error) {
    const errorLog = getRequestErrorLog(
      error,
      '/api/indicators/v2/boundary/',
      params
    );
    addError(errorLog);
    return null;
  }
};

export const getValuationData = async (params: any) => {
  const { markApiCallsAsCompleted } = useApiStore.getState();
  const country = getCountryFromUrl();
  const paramsWithCountry = { ...params, country };
  const parsedParams = new URLSearchParams(paramsWithCountry).toString();

  try {
    const response = await AxiosInstance.get(
      `/api/valuation/v2/valuate?${parsedParams}`
    );
    if (response && response.status === 200) {
      markApiCallsAsCompleted(5);
      // console.log('getValuationData ok - 5', response.data);
      return response.data;
    } else if (response.status === 204) {
      markApiCallsAsCompleted(5);
      return { no_data: true };
    }
  } catch (error) {
    markApiCallsAsCompleted(5);
    const errorLog = getRequestErrorLog(
      error,
      '/api/valuation/v2/valuate?',
      params
    );
    addError(errorLog);
    return null;
  }
};

export const getValuationIndicators = async (params: any) => {
  const { markApiCallsAsCompleted } = useApiStore.getState();
  const country = getCountryFromUrl();
  const paramsWithCountry = { ...params, country };
  let parsedParams = new URLSearchParams(paramsWithCountry).toString();
  const typologyType = new URLSearchParams(paramsWithCountry).get(
    'typology_type'
  );

  if (typologyType == '1' || typologyType == '2' || typologyType == '5') {
    parsedParams = `${parsedParams}&construction_type=2`;
  } else {
    parsedParams = `${parsedParams}&construction_type=0`;
  }

  try {
    const response = await AxiosInstance.get(
      `/api/indicators/v4/list?operation=1&category_id=0&fallback=time_on_market,yield_rental&fallback_by=period_id&project=time_on_market,operation,boundary_id,yield_rental,boundary_type&${parsedParams}`
    );
    if (response && response.status === 200) {
      markApiCallsAsCompleted(6);
      // console.log('getValuationIndicators ok - 6', response.data);
      return response.data.results;
    }
  } catch (error) {
    markApiCallsAsCompleted(6);
    const errorLog = getRequestErrorLog(
      error,
      '/api/indicators/v4/list?',
      params
    );
    addError(errorLog);
    return null;
  }
};

export const getHistogramIndicators = async (params: any) => {
  const { markApiCallsAsCompleted } = useApiStore.getState();
  const country = getCountryFromUrl();
  const paramsWithCountry = { ...params, country };
  let parsedParams = new URLSearchParams(paramsWithCountry).toString();
  const paramsURL = new URLSearchParams(window.location.search);
  const listingType = paramsURL.get('listing_type');

  if (listingType == '1' || listingType == '2' || listingType == '5') {
    parsedParams = `${parsedParams}&construction_type=2`;
  } else {
    parsedParams = `${parsedParams}&construction_type=0`;
  }

  let projectParams =
    'operation,boundary_id,output_priceunit_histogram_bucket1_units_perc,output_priceunit_histogram_bucket2_units_perc,output_priceunit_histogram_bucket3_units_perc,output_priceunit_histogram_bucket4_units_perc,output_priceunit_histogram_bucket4,output_priceunit_histogram_bucket5_units_perc,output_priceunit_histogram_bucket6_units_perc,output_priceunit_histogram_bucket7_units_perc,output_priceunit_histogram_bucket8_units_perc,output_priceunit_histogram_bucket9_units_perc,output_priceunit_histogram_bucket0,output_priceunit_histogram_bucket0_units_perc,output_priceunit_histogram_bucket1,output_priceunit_histogram_bucket2,output_priceunit_histogram_bucket3,output_priceunit_histogram_bucket5,output_priceunit_histogram_bucket6,output_priceunit_histogram_bucket7,output_priceunit_histogram_bucket8,output_priceunit_histogram_bucket9,output_priceunit';

  if (listingType == '7' || listingType == '11') {
    projectParams = projectParams.replace(
      /output_priceunit/g,
      'output_pricemean'
    );
  }

  try {
    const response = await AxiosInstance.get(
      `/api/indicators/v4/list?timeout=20&category_id=0&project=${projectParams}&${parsedParams}`
    );
    if (response && response.status === 200) {
      markApiCallsAsCompleted(7);
      // console.log('getHistogramIndicators ok - 7', response.data);
      return response.data.results;
    }
  } catch (error) {
    markApiCallsAsCompleted(7);
    const errorLog = getRequestErrorLog(
      error,
      '/api/indicators/v4/list?',
      params
    );
    addError(errorLog);
    return null;
  }
};

export const getComparablesECO = async (params: any) => {
  const country = getCountryFromUrl();
  const paramsWithCountry = { ...params, country };
  let parsedParams = new URLSearchParams(paramsWithCountry).toString();
  
  // Add listing_type from URL as typology_type parameter
  const urlParams = new URLSearchParams(window.location.search);
  const listingType = urlParams.get('listing_type');
  if (listingType) {
    parsedParams = `${parsedParams}&typology_type=${listingType}`;
  }
  
  try {
    const response = await AxiosInstance.get(
      `/api/comparables/v3/list?limit=20&n_images=1&sort_by=distance&project=_id,id,location,retail_listing,property_type_id,address,admin4,admin5,area,useful_area,floor,cat_floor,n_rooms,n_baths,construction_year,last_price,last_pm2,last_price_with_correction,last_price_pm2_with_correction,date_main,build_status_id,distance, url,comparison_type,images,source_id,distance,has_elevator,has_terrace,has_storage,has_garage,has_air_conditioner,has_pool,is_exterior,has_tenant,has_vigilance,has_electricity,has_pavement,has_street_lighting,has_natural_gas,has_sewerage,has_water,has_smoke&${parsedParams}`
    );
    if (response && response.status === 200) {
      // console.log('getComparablesECO ok', response.data);
      return response.data;
    }
  } catch (error) {
    const errorLog = getRequestErrorLog(
      error,
      '/api/comparables/v3/list?',
      params
    );
    addError(errorLog);
    return null;
  }
};

export const getSimilarComparables = async (params: any) => {
  const { markApiCallsAsCompleted } = useApiStore.getState();
  const country = getCountryFromUrl();
  const paramsWithCountry = { ...params, country };
  let parsedParams = new URLSearchParams(paramsWithCountry).toString();

  // Add listing_type from URL as typology_type parameter
  const urlParams = new URLSearchParams(window.location.search);
  const listingType = urlParams.get('listing_type');
  if (listingType) {
    parsedParams = `${parsedParams}&typology_type=${listingType}`;
  }

  // Add target parameters from URL if they exist
  const targetParamsMap: Record<string, string> = {
    area: 'target_area',
    n_baths: 'target_baths',
    n_rooms: 'target_rooms',
    property_type: 'target_property_type',
    floor: 'target_floor',
    has_garage: 'target_has_garage',
    has_elevator: 'target_has_elevator',
    has_pool: 'target_has_pool'
  };

  const targetParams = new URLSearchParams();
  Object.entries(targetParamsMap).forEach(([urlParam, targetParam]) => {
    const value = urlParams.get(urlParam);
    if (value && value !== 'undefined' && value !== undefined) {
      // Convert to number and round if it's a float
      const numValue = parseFloat(value);
      const finalValue = !isNaN(numValue) && numValue % 1 !== 0 
        ? Math.round(numValue).toString() 
        : value;
      targetParams.append(targetParam, finalValue);
    }
  });

  const targetParamsString = targetParams.toString();
  if (targetParamsString) {
    parsedParams = `${parsedParams}&${targetParamsString}`;
  }

  try {
    const response = await AxiosInstance.get(
      `/api/comparables/v3/list?is_comparable=1&ascending=0&sort_by=similarity&project=_id,id,distance,area,last_price,last_pm2,last_price_with_correction,last_price_pm2_with_correction&${parsedParams}`
    );
    if (response && response.status === 200) {
      markApiCallsAsCompleted(8);
      return response.data;
    }
  } catch (error) {
    markApiCallsAsCompleted(8);
    const errorLog = getRequestErrorLog(
      error,
      '/api/comparables/v3/list?',
      params
    );
    addError(errorLog);
    return null;
  }
};

export const getFinancialIndicators = async (params: any) => {
  const { markApiCallsAsCompleted } = useApiStore.getState();
  const country = getCountryFromUrl();
  const paramsWithCountry = { ...params, country };
  const searchParams = new URLSearchParams(paramsWithCountry);

  let parsedParams = searchParams.toString();

  parsedParams = `${parsedParams}&construction_type=0,2`;

  try {
    const response = await AxiosInstance.get(
      `/api/indicators/v4/list?timeout=20&category_id=0&fallback=income_household_gross,md_affordability_years,perc_unemployment&fallback_by=boundary_id&project=typology_type,construction_type,boundary_type,output_priceunit_yoy,output_priceunit,income_household_gross,md_affordability_years,perc_unemployment&${parsedParams}`
    );
    if (response && response.status === 200) {
      markApiCallsAsCompleted(9);
      // console.log('getFinancialIndicators ok - 9', response.data);
      return response.data.results;
    }
  } catch (error) {
    markApiCallsAsCompleted(9);
    const errorLog = getRequestErrorLog(
      error,
      '/api/indicators/v4/list?',
      params
    );
    addError(errorLog);
    return null;
  }
};

export const getOfferDemandIndicators = async (params: any) => {
  const { markApiCallsAsCompleted } = useApiStore.getState();
  const country = getCountryFromUrl();
  const paramsWithCountry = { ...params, country };
  let parsedParams = new URLSearchParams(paramsWithCountry).toString();

  const typologyType = new URLSearchParams(paramsWithCountry).get(
    'typology_type'
  );

  if (typologyType == '1' || typologyType == '2' || typologyType == '5') {
    parsedParams = `${parsedParams}&construction_type=2`;
  } else {
    parsedParams = `${parsedParams}&construction_type=0`;
  }

  try {
    const response = await AxiosInstance.get(
      `/api/indicators/v4/list?timeout=20&category_id=0,1,2,3,4,5,6&operation=0,1&project=stock_units,output_units,operation,category_id,mean_housing_age&${parsedParams}`
    );
    if (response && response.status === 200) {
      markApiCallsAsCompleted(10);
      // console.log('getOfferDemandIndicators ok - 10', response.data);
      return response.data.results;
    }
  } catch (error) {
    markApiCallsAsCompleted(10);
    const errorLog = getRequestErrorLog(
      error,
      '/api/indicators/v4/list?',
      params
    );
    addError(errorLog);
    return null;
  }
};

export const getZonePopulationIndicators = async (params: any) => {
  const { markApiCallsAsCompleted } = useApiStore.getState();
  const country = getCountryFromUrl();
  const paramsWithCountry = { ...params, country };
  const searchParams = new URLSearchParams(paramsWithCountry);
  searchParams.set('typology_type', '1');

  let parsedParams = searchParams.toString();

  try {
    const response = await AxiosInstance.get(
      `/api/indicators/v4/list?timeout=20&category_id=0&project=operation,perc_national_local,perc_national_america,perc_national_europe,perc_national_africa,perc_national_asia,perc_national_oceania,perc_age_85_89,perc_age_90_94,perc_age_95_99,perc_age_100_m,perc_age_65_69,perc_age_70_74,perc_age_75_79,perc_age_80_84,perc_age_50_54,perc_age_55_59,perc_age_60_64,perc_age_40_44,perc_age_45_49,perc_age_25_29,perc_age_30_34,perc_age_35_39,perc_age_15_19,perc_age_20_24,perc_edu_level3,perc_edu_level2,perc_edu_level1,mean_population_age&${parsedParams}`
    );
    if (response && response.status === 200) {
      markApiCallsAsCompleted(11);
      // console.log('getZonePopulationIndicators ok - 11', response.data);
      return response.data.results;
    }
  } catch (error) {
    markApiCallsAsCompleted(11);
    const errorLog = getRequestErrorLog(
      error,
      '/api/indicators/v4/list?',
      params
    );
    addError(errorLog);
    return null;
  }
};

export const getZoneIndicators = async (params: any) => {
  const { markApiCallsAsCompleted } = useApiStore.getState();
  const country = getCountryFromUrl();
  const paramsWithCountry = { ...params, country };
  const parsedParams = new URLSearchParams(paramsWithCountry).toString();

  try {
    const response = await AxiosInstance.get(
      `/api/indicators/v2/pois?max_distance=500&pois_types=facilities_sports,nature_green_areas,facilities_social,facilities_cultural,transportation_public,amenities_catering,commerce_special,commerce_basic,facilities_healthcare_basic,facilities_healthcare_special,facilities_education_basic,facilities_education_higher&agg=true&rating=true&${parsedParams}`
    );
    if (response && response.status === 200) {
      markApiCallsAsCompleted(12);
      // console.log('getZoneIndicators ok - 12', response.data);
      return response.data;
    }
  } catch (error) {
    markApiCallsAsCompleted(12);
    const errorLog = getRequestErrorLog(
      error,
      '/api/indicators/v2/pois?',
      params
    );
    addError(errorLog);
    return null;
  }
};

export const sendErrorReport = async (errorsReport: ErrorLog) => {
  try {
    const response = await AxiosInstance.post(
      '/api/backoffice/v1/users/report-logs',
      errorsReport
    );
    if (response && response.status === 200) {
      return response.data;
    }
  } catch (error) {
    console.error(error);
  }
};
